import { produce } from 'immer'
import {
  GET_ORG_STATS_FAILURE,
  GET_ORG_STATS_INIT,
  GET_ORG_STATS_SUCCESS,
  GET_TOTAL_AVAILABLE_CREDIT_FAILURE,
  GET_TOTAL_AVAILABLE_CREDIT_INIT,
  GET_TOTAL_AVAILABLE_CREDIT_SUCCESS,
  GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE,
  GET_TOTAL_AVAILABLE_CREDIT_V2_INIT,
  GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS,
  GetOrgStatsActions,
  GetTotalAvailableCreditActions,
  GetTotalAvailableCreditV2Actions,
} from 'src/app/Home/redux/home.actions'
import { AlertsResponse } from 'src/models/alerts.models'
import { NeedsApprovalResponse } from 'src/models/needsApproval.models'
import { homeRouteProps } from 'src/navigation/routeProps'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import TransactionsSnapshotPaginatedDataReducer, {
  Actions as TransactionsSnapshotPaginatedDataActions,
  initialState as initialTransactionsSnapshotPaginatedDataState,
  TransactionsSnapshotPaginatedDataState,
} from 'src/redux/transactionsSnapshotPaginatedData.reducer'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface HomeState {
  alerts: AlertsResponse & {
    loading: boolean
  }
  needsApproval: NeedsApprovalResponse & {
    loading: boolean
    complete: boolean
  }
  totalAvailableCredit: {
    loading: boolean
    totalAvailableCreditCents?: number
    totalAvailableCreditCentsUSD?: number | null
    totalAvailableCreditCentsCAD?: number | null
  }
  orgStats: {
    loading: boolean
    total?: number
    owner?: number
    admin?: number
    cardManager?: number
    bookkeeper?: number
    member?: number
    guest?: number
    topLevelError?: string
  }
  transactionsSnapshotPaginatedData: TransactionsSnapshotPaginatedDataState
}

export const initialState: HomeState = {
  alerts: {
    loading: false,
  },
  needsApproval: {
    loading: false,
    complete: false,
  },
  totalAvailableCredit: {
    loading: false,
  },
  orgStats: {
    loading: false,
  },
  transactionsSnapshotPaginatedData:
    initialTransactionsSnapshotPaginatedDataState,
}

export type Actions =
  | TransactionsSnapshotPaginatedDataActions
  | GetTotalAvailableCreditActions
  | GetTotalAvailableCreditV2Actions
  | GetOrgStatsActions

const CoreHomeReducer = (state = initialState, action: Actions): HomeState => ({
  ...produce(state, (draft) => {
    switch (action.type) {
      case GET_TOTAL_AVAILABLE_CREDIT_INIT:
      case GET_TOTAL_AVAILABLE_CREDIT_V2_INIT: {
        draft.totalAvailableCredit.loading = true
        return draft
      }

      case GET_TOTAL_AVAILABLE_CREDIT_SUCCESS: {
        draft.totalAvailableCredit.loading = false
        draft.totalAvailableCredit.totalAvailableCreditCents =
          action.payload.totalAvailableCreditCents
        return draft
      }

      case GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS: {
        draft.totalAvailableCredit.loading = false
        draft.totalAvailableCredit.totalAvailableCreditCentsUSD =
          action.payload.totalAvailableCreditCentsUSD
        draft.totalAvailableCredit.totalAvailableCreditCentsCAD =
          action.payload.totalAvailableCreditCentsCAD
        return draft
      }

      case GET_TOTAL_AVAILABLE_CREDIT_FAILURE:
      case GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE: {
        draft.totalAvailableCredit.loading = false
        return draft
      }

      case GET_ORG_STATS_INIT: {
        draft.orgStats.loading = true
        draft.orgStats.topLevelError = undefined
        return draft
      }

      case GET_ORG_STATS_SUCCESS: {
        draft.orgStats.loading = false
        draft.orgStats.total = action.payload.total
        draft.orgStats.owner = action.payload.owner
        draft.orgStats.admin = action.payload.admin
        draft.orgStats.cardManager = action.payload.cardManager
        draft.orgStats.bookkeeper = action.payload.bookkeeper
        draft.orgStats.member = action.payload.member
        draft.orgStats.guest = action.payload.guest
        return draft
      }

      case GET_ORG_STATS_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.orgStats.loading = false
        draft.orgStats.topLevelError = errors.topLevelError
        return draft
      }
    }
  }),
  transactionsSnapshotPaginatedData: TransactionsSnapshotPaginatedDataReducer(
    state.transactionsSnapshotPaginatedData,
    action as TransactionsSnapshotPaginatedDataActions,
  ),
})

const HomeReducer = createBackgroundReducer(
  homeRouteProps,
  initialState,
  CoreHomeReducer,
)

export default HomeReducer
